import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Divider,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getCoverageOverview } from '../../services/apiService';

const WINDOW_PRESETS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '1y' },
];
const DEFAULT_WINDOW = 30;

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const pctColor = (pct) => {
  if (pct >= 75) return 'success.main';
  if (pct >= 40) return 'warning.main';
  return 'error.main';
};

const KpiCard = ({ label, primary, secondary, accent }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      borderLeft: '4px solid',
      borderLeftColor: accent || 'primary.main',
    }}
  >
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ letterSpacing: 0.6, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 600, lineHeight: 1.1 }}>
      {primary}
    </Typography>
    {secondary && (
      <Typography variant="caption" color="text.secondary">
        {secondary}
      </Typography>
    )}
  </Paper>
);

const ModuleTile = ({ label, coveragePct, classCount, onClick }) => {
  const color = pctColor(coveragePct);
  return (
    <Paper
      elevation={1}
      onClick={onClick}
      sx={{
        position: 'relative',
        px: 1.25,
        pt: 1,
        pb: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        borderRadius: 1.5,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': onClick
          ? { transform: 'translateY(-1px)', boxShadow: 3 }
          : undefined,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          lineHeight: 1.25,
          fontSize: 13,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: 32, // reserves 2 lines so single-line titles still line up
        }}
        title={label}
      >
        {label}
      </Typography>
      <Box display="flex" alignItems="baseline" gap={0.75}>
        <Typography sx={{ fontWeight: 700, color, fontSize: 18, lineHeight: 1 }}>
          {coveragePct.toFixed(1)}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          · {fmt(classCount)} classes
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          backgroundColor: 'action.hover',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${Math.min(100, coveragePct)}%`,
            backgroundColor: color,
            transition: 'width 0.3s',
          }}
        />
      </Box>
    </Paper>
  );
};

const CoverageLanding = ({ onOpenTree, initialWindowDays = DEFAULT_WINDOW, onWindowChange }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [windowDays, setWindowDays] = useState(initialWindowDays);
  const [groupBy, setGroupBy] = useState('section'); // 'section' (MD area, default) | 'module' (dash-rollup)

  const goTree = (filter) => {
    if (typeof onOpenTree === 'function') onOpenTree(windowDays, filter);
    else navigate('/coverage/tree');
  };

  const load = async (w = windowDays) => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await getCoverageOverview({ windowDays: w });
      setData(resp?.data || null);
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(windowDays);
    if (typeof onWindowChange === 'function') onWindowChange(windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  const kpis = data?.kpis;
  const build = data?.build;
  const byModule = data?.byModule || [];
  const bySection = data?.bySection || [];
  const topUncovered = data?.topUncoveredClasses || [];
  const groupedRows = groupBy === 'section' ? bySection : byModule;
  const groupKey = groupBy === 'section' ? 'section' : 'module';

  const buildLabel = build
    ? `${build.branch || '?'} @ ${(build.commit_sha || '').slice(0, 8) || build.build_version || '?'}`
    : '—';

  const syncedAgo = build?.committed_at
    ? formatDistanceToNow(new Date(build.committed_at), { addSuffix: true })
    : refreshedAt
    ? formatDistanceToNow(refreshedAt, { addSuffix: true })
    : '—';

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box mb={3} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Coverage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drill4J-backed code coverage. How are we doing across the last{' '}
            <strong>{windowDays}</strong> days?
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Tooltip
            title="Coverage window — a method is 'covered' if any test, on any build, touched it within this period. Default 30 days."
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Window
              </Typography>
            </Stack>
          </Tooltip>
          <ToggleButtonGroup
            value={windowDays}
            exclusive
            size="small"
            onChange={(_, v) => v && setWindowDays(v)}
          >
            {WINDOW_PRESETS.map((p) => (
              <ToggleButton key={p.value} value={p.value} sx={{ px: 1.2, py: 0.4 }}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => load(windowDays)} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AccountTreeIcon />}
            onClick={() => goTree()}
          >
            Drill Into Coverage
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Overview panel — KPI strip */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
            COVERAGE OVERVIEW
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Build: <code>{buildLabel}</code> · Window: <strong>last {windowDays} days</strong> · Synced: {syncedAgo}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6} md={2.4}>
            <KpiCard
              label="Overall"
              primary={
                <Box component="span" sx={{ color: kpis ? pctColor(kpis.overallCoveragePct) : 'text.primary' }}>
                  {kpis ? `${kpis.overallCoveragePct}%` : '—'}
                </Box>
              }
              secondary="of methods covered"
              accent={kpis ? pctColor(kpis.overallCoveragePct) : 'primary.main'}
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <KpiCard
              label="Classes"
              primary={kpis ? `${fmt(kpis.coveredClasses)} / ${fmt(kpis.totalClasses)}` : '—'}
              secondary="classes with ≥1 covered method"
              accent="info.main"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <KpiCard
              label="Methods"
              primary={kpis ? `${fmt(kpis.coveredMethods)} / ${fmt(kpis.totalMethods)}` : '—'}
              secondary="methods exercised"
              accent="info.main"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <KpiCard
              label="Probes"
              primary={kpis ? fmt(kpis.totalProbes) : '—'}
              secondary="total instrumented probes"
              accent="secondary.main"
            />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <KpiCard
              label="Uncovered Classes"
              primary={kpis ? fmt(kpis.uncoveredClasses) : '—'}
              secondary="classes with 0% coverage"
              accent="error.main"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Coverage by Module / Area */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
            COVERAGE BY {groupBy === 'section' ? 'AREA' : 'MODULE'}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Tooltip title="Group by Module (Files, Admin, …) or by broader functional Area (Document & File Management, AI / ML / Cognitive Services, …) from docs/coverage/module-structure.md.">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={groupBy}
                onChange={(_, v) => v && setGroupBy(v)}
              >
                <ToggleButton value="section" sx={{ px: 1.2, py: 0.4 }}>Area</ToggleButton>
                <ToggleButton value="module" sx={{ px: 1.2, py: 0.4 }}>Module</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              sorted by coverage, lowest first
            </Typography>
            <MuiLink
              component="button"
              onClick={() => goTree()}
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              Explore in Coverage Tree <ArrowOutwardIcon fontSize="inherit" />
            </MuiLink>
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {groupedRows.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No {groupBy === 'section' ? 'area' : 'module'} data yet.
          </Typography>
        )}
        <Grid container spacing={2}>
          {groupedRows.map((m) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={m[groupKey]}>
              <ModuleTile
                label={m[groupKey]}
                coveragePct={m.coveragePct}
                classCount={m.classCount}
                onClick={() => goTree({ [groupKey]: m[groupKey] })}
              />
            </Grid>
          ))}
        </Grid>
        {groupedRows.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {groupBy === 'section'
              ? <>Areas come from the section headers in <code>docs/coverage/module-structure.md</code> (e.g. <em>Document &amp; File Management</em>).</>
              : <>Modules collapse the MD's <code>Module – Submodule</code> rows up to the parent (so <em>Files – Document</em>, <em>Files – Event</em>, … all roll into <strong>Files</strong>).</>}
            {' '}Tiles labelled <strong>{groupBy === 'section' ? 'Uncategorised' : 'No module'}</strong> sit outside the taxonomy.
          </Typography>
        )}
      </Paper>

      {/* Top 10 Uncovered Classes */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
            TOP 10 UNCOVERED CLASSES
          </Typography>
          <MuiLink
            component="button"
            onClick={() => goTree()}
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            View all in tree <ArrowOutwardIcon fontSize="inherit" />
          </MuiLink>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Module</TableCell>
                <TableCell align="right">Methods</TableCell>
                <TableCell align="right">Probes</TableCell>
                <TableCell align="right">Cov %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topUncovered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No uncovered classes found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {topUncovered.map((c) => (
                <TableRow
                  key={c.classFqn}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => goTree(c.module ? { module: c.module } : undefined)}
                >
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.classFqn}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.module} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">{fmt(c.methodCount)}</TableCell>
                  <TableCell align="right">{fmt(c.probesCount)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: pctColor(c.coveragePct) }}
                    >
                      {c.coveragePct.toFixed(1)}%
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer help */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        <strong>How "covered" is measured:</strong> a method counts as covered if any test, on
        any build, touched a method with the same <em>signature</em> within the selected window
        (currently {windowDays} days). The denominator is always the latest build's method set,
        so deleted methods drop out and newly-added methods correctly appear as uncovered.{' '}
        <strong>Note:</strong> synthetic/runtime-generated methods (<code>&lt;clinit&gt;</code>,{' '}
        <code>lambda$…</code>, <code>access$…</code>) are excluded from coverage calculations
        to reflect only hand-written code. See <code>Drill4J-Coverage-Methodology.md</code> for
        the full rules and SQL.
      </Typography>
    </Box>
  );
};

export default CoverageLanding;
